import { useEffect, useState } from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { Close, Menu } from '@mui/icons-material';
import { Box, Avatar, Button, IconButton, Badge, Drawer } from '@mui/material';
import AdvisoryCloudLogo from '../AdvisoryCloudLogo2';
import { useAccount } from '../../contexts/Account';
import CalendarSyncMenu from '../CalendarSyncMenu';
import FreeTrialBanner from '../Company/FreeTrialBanner';
import MobileMenu from './MobileMenu';
import DefaultAdvisorAvatar from '../../icons/user-avatar.svg';
import ProfileMenu from './ProfileMenu';
import { getAdvisorQueryParams } from '../../utils/queryParams';
import { AccountType, SnackbarType } from '../../services/utils/types';
import ACSnackbar from '../ACSnackbar';
import UpsellBanner from './UpsellBanner';
import HoverMenuItem from '../HoverMenuItem';
import eventBus from '../../eventBus';
import {
  closeChild,
  getAccessToken,
  isLinkedinAuthenticated,
} from '../../services/LinkedIn';
import LinkedInPostModal from '../LinkedInPostModal';
import ProfessionalGoals from '../../scenes/AdvisorDashboardV2/ProfessionalGoals';

const TopBar = (props) => {
  const history = useHistory();
  const { menuItems } = props;
  const {
    accountDetails,
    signOut,
    isFreeTrial,
    isFreemium,
    conversations,
    currentPlan,
    type,
  } = useAccount();
  const { specialOffersUrl } = currentPlan;
  function getUnreadMessages() {
    let totalUnread = 0;
    conversations.forEach((conv) => {
      totalUnread += conv.unreadCount;
    });
    return totalUnread + (accountDetails.readCeoMessage ? 0 : 1);
  }
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [goalsDrawerOpen, setGoalsDrawerOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState(SnackbarType.SUCCESS);
  const [googleSync, setGoogleSync] = useState(
    accountDetails?.calendarTokens?.google || false
  );
  const [appleSync, setAppleSync] = useState(
    accountDetails?.calendarTokens?.apple || false
  );
  const [linkedInPostModal, setLinkedInPostModal] = useState(false);

  const { pathname, search } = useLocation();
  const fullPath = `${pathname}${search}`;

  const showSnackbar = (message, severity = SnackbarType.SUCCESS) => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackbarOpen(true);
  };

  const redirectWithParams = (url) => {
    window.location.assign(
      `${url}?${getAdvisorQueryParams({
        accountDetails,
        isFreeTrial,
        isFreemium,
      })}`
    );
  };

  const menuItemStyle = {
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    padding: '5px 18px',
    borderRadius: '8px',
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    if (typeof signOut === 'function') {
      signOut();
    } else {
      console.log('signOut function is not provided');
    }
  };

  async function linkedInResponse(linkedinResponse) {
    if (linkedinResponse === 201) {
      showSnackbar('Your profile is shared successfully');
    } else if (linkedinResponse === 422) {
      showSnackbar(
        'You have already posted this content, either update the post or try again in 10 minutes',
        SnackbarType.ERROR
      );
    } else {
      showSnackbar(
        'An error occurred while sharing your profile',
        SnackbarType.ERROR
      );
    }
  }

  useEffect(() => {
    window.linkedinWait = true;

    const interval = setInterval(() => {
      if (window.linkedinSuccess) {
        clearInterval(interval);
        closeChild();
        getAccessToken(window.linkedinSuccess);
        eventBus.dispatch('openLinkedInPostModal');
      }
    }, 600);

    if (isLinkedinAuthenticated()) {
      window.linkedinWait = undefined;
      clearInterval(interval);
    }
    return () => {
      window.linkedinWait = undefined;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    eventBus.on('openProfessionalGoals', () => {
      setGoalsDrawerOpen(true);
    });
    return () => {
      eventBus.remove('openProfessionalGoals');
    };
  }, []);

  function MenuOptions() {
    return (
      <>
        {menuItems.map((item, index) => {
          if (item.options) {
            return (
              <HoverMenuItem
                key={index}
                label={item.label}
                id={item.id}
                options={item.options}
                paths={item.paths}
                menuItemStyle={menuItemStyle}
                closeOnClick={item.closeOnClick}
                containerWidth={item.containerWidth}
                leftOptionsPosition={item.leftOptionsPosition}
                mainOnClick={item.mainOnClick}
                avoidMainCLick={item.avoidMainCLick}
              />
            );
          }
          const isActive = fullPath === item.to;
          if (item.onClick) {
            return (
              <Button
                id={item.id}
                key={index}
                onClick={item.onClick}
                sx={{
                  ...menuItemStyle,
                  color: isActive ? '#2B71F6' : '#2E3B56',
                  backgroundColor: isActive ? '#F4F8FF' : 'transparent',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {item.label}
              </Button>
            );
          }

          return (
            <NavLink
              id={item.id}
              key={index}
              to={item.to || '#'}
              style={{
                ...menuItemStyle,
                color: isActive ? '#2B71F6' : '#2E3B56',
                backgroundColor: isActive ? '#F4F8FF' : 'transparent',
              }}
            >
              {item.label}
            </NavLink>
          );
        })}
      </>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        zIndex: 1200,
        width: '100%',
        backgroundColor: '#fff',
        overscrollBehavior: 'contain',
      }}
    >
      <Box
        sx={{
          minHeight: isFreeTrial ? '60px' : specialOffersUrl ? '38px' : '0px',
        }}
      >
        {!isFreeTrial && <UpsellBanner />}
        {isFreeTrial && <FreeTrialBanner show={isFreeTrial} />}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: { xxs: '10px 3px 10px 5px', sm: '10px 25px' },
          backgroundColor: '#fff',
          borderBottom: '1px solid #ddd',
          height: 60,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AdvisoryCloudLogo
            height={'27px'}
            removeHyperlink
            onClick={() => {
              history.push(`/dashboard`);
            }}
            cursor="default"
          />
        </Box>

        <Box
          sx={{
            display: 'none',
            alignItems: 'center',
            '@media (min-width: 800px)': {
              display: 'flex',
            },
          }}
        >
          <MenuOptions />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: { xxs: '80px', md: '170px' },
            gap: '15px',
            justifyContent: 'end',
          }}
        >
          {/* DO NOT DELETE, WILL BE REUSED WITH NEW INFO */}
          {/* Also, remove the temporal padding from src/components/TopBar/UpsellBanner.jsx when re use the button */}
          {/* <IconButton
            sx={{
              display: 'flex',
              '@media (min-width: 1110px)': {
                display: 'none',
              },
              color: '#2563eb',
            }}
            onClick={navigateToPersonalAssistance}
          >
            <PhoneInTalkOutlinedIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<PhoneInTalkOutlinedIcon />}
            id="sync-calendar-button"
            disabled={isLoading}
            sx={{
              display: 'none',
              '@media (min-width: 1110px)': {
                display: 'flex',
              },
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: '600',
              padding: '0',
              borderRadius: '4px',
              borderColor: '#2B71F6',
              color: '#2B71F6',
              width: 220,
              height: 32,
              '&:hover': {
                borderColor: '#1e3a8a',
                backgroundColor: '#f1f5ff',
              },
            }}
            onClick={navigateToPersonalAssistance}
          >
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LoadingLogoSkeleton />
              </div>
            ) : (
              'Personal Assistance'
            )}
          </Button> */}
          {/* <IconButton
            sx={{
              display: 'flex',
              color: '#2E3B56',
            }}
            id="top-menu-help-icon"
          >
            <HelpOutlineIcon />
          </IconButton> */}
          <Badge
            color="error"
            variant="dot"
            invisible={getUnreadMessages() <= 0}
            sx={{
              '& .MuiBadge-badge': {
                top: '85%',
                right: '15%',
                transform: 'translate(50%, -50%)',
              },
            }}
          >
            <Avatar
              src={
                accountDetails.image?.imageKey
                  ? `${process.env.REACT_APP_IMAGES_URL}${accountDetails.image.imageKey}`
                  : DefaultAdvisorAvatar
              }
              alt={accountDetails.displayName}
              sx={{
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: { xxs: 'none', md: 'block' },
              }}
              onClick={handleProfileMenuOpen}
            />
          </Badge>
          <IconButton
            id="menu-icon-button"
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'block', md: 'none' }, color: '#2563eb' }}
          >
            <Menu />
          </IconButton>
          <MobileMenu
            isDrawerOpen={isDrawerOpen}
            toggleDrawer={setDrawerOpen}
            menuItems={menuItems}
            pathname={pathname}
          />
        </Box>

        <ProfileMenu
          profileAnchorEl={profileAnchorEl}
          handleProfileMenuClose={handleProfileMenuClose}
          handleLogout={handleLogout}
        />

        <Drawer
          anchor="right"
          open={goalsDrawerOpen}
          onClose={() => setGoalsDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              padding: '16px 20px',
              backgroundColor: '#f9fbff',
            },
          }}
        >
          <Close
            onClick={() => setGoalsDrawerOpen(false)}
            sx={{
              display: { xxs: 'block', sm: 'none' },
              position: 'absolute',
              top: '16px',
              left: '16px',
            }}
          />
          <ProfessionalGoals />
        </Drawer>

        <CalendarSyncMenu
          anchorEl={calendarAnchorEl}
          setAnchorEl={setCalendarAnchorEl}
          syncStates={{
            googleSync,
            appleSync,
          }}
          setSyncStates={{
            setGoogleSync,
            setAppleSync,
          }}
          setIsLoading={setIsLoading}
          showSnackbar={showSnackbar}
        />
        <ACSnackbar
          style={{ marginTop: isFreemium ? '50px' : '0px' }}
          open={snackbarOpen}
          text={snackMessage}
          severity={snackSeverity}
          onClose={() => {
            setSnackbarOpen(false);
          }}
          autoHideDuration={5000}
        />
      </Box>
      {linkedInPostModal ? (
        <>
          <LinkedInPostModal
            linkedInPostModal={linkedInPostModal}
            setLinkedInPostModal={setLinkedInPostModal}
            linkedInResponse={linkedInResponse}
            fontFamily={'Poppins'}
            shareProfile
          />
        </>
      ) : null}
    </Box>
  );
};

export default TopBar;
